import React, { useEffect, useState } from 'react'
import PageHeader from '../../PageHeader/PageHeader'
import { HitApi } from '../../Api/ApiHit'
import { searchAttendance, searchUser, updateAttendance, addAttendance } from '../../constant/Constant'
import { getSelectedUnit } from '../../storage/Storage'
import AppTable from '../../components/AppTable/AppTable'
import toast from 'react-hot-toast'
import { Calendar, RefreshCw, Users, DollarSign, TrendingDown, CheckCircle, XCircle, Eye, X, Pencil } from 'lucide-react'
import { getElevateUser } from '../../storage/Storage'
import { exportToCSV, exportToPDF } from '../../utils/exportUtils'

function AttendanceReport() {
    const [users, setUsers] = useState([])
    const [attendance, setAttendance] = useState([])
    const [salaryData, setSalaryData] = useState([])
    const [isLoading, setIsLoading] = useState(false)

    // Date range filter states
    const [selectedFilter, setSelectedFilter] = useState('thisMonth')
    const [showCustomRange, setShowCustomRange] = useState(false)
    const [startDate, setStartDate] = useState('')
    const [endDate, setEndDate] = useState('')

    // Modal states for daily attendance view
    const [showAttendanceModal, setShowAttendanceModal] = useState(false)
    const [selectedEmployee, setSelectedEmployee] = useState(null)
    const [dailyAttendanceData, setDailyAttendanceData] = useState([])

    // Edit modal states
    const [showEditModal, setShowEditModal] = useState(false)
    const [editingAttendance, setEditingAttendance] = useState(null)
    const [editFormData, setEditFormData] = useState({
        dayType: '',
        remarks: ''
    })

    // Check if current user is SuperAdmin
    const currentUser = getElevateUser()
    const isSuperAdmin = currentUser?.roleData?.roleType === 'SuperAdmin' && currentUser?.userType === 'SuperAdmin'

    // Calculated date range for display
    const [dateRange, setDateRange] = useState({ start: null, end: null })

    const weekDays = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']

    const filterOptions = [
        { label: 'Today', value: 'today' },
        { label: 'Yesterday', value: 'yesterday' },
        { label: 'This Week', value: 'thisWeek' },
        { label: 'This Month', value: 'thisMonth' },
        { label: 'Last Month', value: 'lastMonth' },
        { label: 'Last 7 Days', value: 'last7Days' },
        { label: 'Last 30 Days', value: 'last30Days' },
        { label: 'Custom Range', value: 'customRange' },
    ]

    const handleFilterChange = (value) => {
        setSelectedFilter(value)
        if (value === 'customRange') {
            setShowCustomRange(true)
        } else {
            setShowCustomRange(false)
            setStartDate('')
            setEndDate('')
        }
    }

    const handleApplyCustomRange = () => {
        if (startDate && endDate) {
            fetchData()
        } else {
            toast.error('Please select both start and end dates')
        }
    }

    // Get date range based on selected filter
    const getDateRange = () => {
        const now = new Date()
        let start, end

        switch (selectedFilter) {
            case 'today':
                start = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0)
                end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59)
                break

            case 'yesterday':
                const yesterday = new Date(now)
                yesterday.setDate(now.getDate() - 1)
                start = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate(), 0, 0, 0)
                end = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate(), 23, 59, 59)
                break

            case 'thisWeek':
                const weekStart = new Date(now)
                const day = now.getDay()
                const diff = now.getDate() - day + (day === 0 ? -6 : 1)
                weekStart.setDate(diff)
                start = new Date(weekStart.getFullYear(), weekStart.getMonth(), weekStart.getDate(), 0, 0, 0)
                end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59)
                break

            case 'thisMonth':
                start = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0)
                end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59)
                break

            case 'lastMonth':
                start = new Date(now.getFullYear(), now.getMonth() - 1, 1, 0, 0, 0)
                end = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59)
                break

            case 'last7Days':
                const last7 = new Date(now)
                last7.setDate(now.getDate() - 6)
                start = new Date(last7.getFullYear(), last7.getMonth(), last7.getDate(), 0, 0, 0)
                end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59)
                break

            case 'last30Days':
                const last30 = new Date(now)
                last30.setDate(now.getDate() - 29)
                start = new Date(last30.getFullYear(), last30.getMonth(), last30.getDate(), 0, 0, 0)
                end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59)
                break

            case 'customRange':
                if (startDate && endDate) {
                    start = new Date(startDate)
                    start.setHours(0, 0, 0, 0)
                    end = new Date(endDate)
                    end.setHours(23, 59, 59, 999)
                } else {
                    start = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0)
                    end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59)
                }
                break

            default:
                start = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0)
                end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59)
        }

        return { start, end }
    }

    // Get formatted date range text
    const getDateRangeText = () => {
        const { start, end } = getDateRange()
        const formatDate = (date) => date.toLocaleDateString('en-IN', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
        })

        if (selectedFilter === 'today') {
            return formatDate(start)
        } else if (selectedFilter === 'yesterday') {
            return formatDate(start)
        }
        return `${formatDate(start)} - ${formatDate(end)}`
    }

    useEffect(() => {
        if (selectedFilter !== 'customRange') {
            fetchData()
        }
    }, [selectedFilter])

    const fetchData = async () => {
        setIsLoading(true)
        const range = getDateRange()
        setDateRange(range)

        try {
            const [usersRes, attendanceRes] = await Promise.all([
                fetchUsers(),
                fetchAttendance(range)
            ])

            if (usersRes && attendanceRes) {
                calculateSalary(usersRes, attendanceRes, range)
                toast.success(`Loaded ${attendanceRes.length} attendance records`)
            }
        } catch (error) {
            console.error('Error fetching data:', error)
            toast.error('Failed to fetch data')
        }
        setIsLoading(false)
    }

    const fetchUsers = () => {
        return new Promise((resolve) => {
            const searchObj = {
                unitIds: getSelectedUnit()?._id
            }

            // If not SuperAdmin, only fetch current user's data
            if (!isSuperAdmin) {
                searchObj._id = getElevateUser()?._id
            }

            const json = {
                page: 1,
                limit: 500,
                search: searchObj,
            }
            HitApi(json, searchUser).then((res) => {
                if (res?.statusCode === 200) {
                    setUsers(res.data.docs)
                    resolve(res.data.docs)
                } else {
                    resolve([])
                }
            }).catch(() => resolve([]))
        })
    }

    const fetchAttendance = (range) => {
        return new Promise((resolve) => {
            const searchObj = {
                unitIds: getSelectedUnit()?._id,
                created: {
                    $gte: range.start.toISOString(),
                    $lte: range.end.toISOString()
                }
            }

            // If not SuperAdmin, only fetch current user's attendance
            if (!isSuperAdmin) {
                searchObj.userId = getElevateUser()?._id
            }

            const json = {
                page: 1,
                limit: 10000,
                search: searchObj,
            }
            HitApi(json, searchAttendance).then((res) => {
                if (res?.statusCode === 200) {
                    setAttendance(res.data.docs)
                    resolve(res.data.docs)
                } else {
                    resolve([])
                }
            }).catch(() => resolve([]))
        })
    }

    // Count specific weekday occurrences in a date range
    const countWeekdayInRange = (startDate, endDate, weekday) => {
        const weekdayIndex = weekDays.indexOf(weekday.toLowerCase())
        if (weekdayIndex === -1) return 0

        let count = 0
        const current = new Date(startDate)
        const end = new Date(endDate)

        while (current <= end) {
            if (current.getDay() === weekdayIndex) {
                count++
            }
            current.setDate(current.getDate() + 1)
        }
        return count
    }

    // Get total days in date range
    const getDaysInRange = (startDate, endDate) => {
        const start = new Date(startDate)
        const end = new Date(endDate)
        // Normalize to midnight for accurate day count
        start.setHours(0, 0, 0, 0)
        end.setHours(0, 0, 0, 0)
        const diffTime = Math.abs(end - start)
        return Math.floor(diffTime / (1000 * 60 * 60 * 24)) + 1
    }

    // Count unique present days for a user based on dayType from backend only
    const countPresentDays = (userId, attendanceData) => {
        const dayValues = {}
        const dayTypes = {}

        attendanceData.forEach(record => {
            const recordUserId = record.userId?._id || record.userId
            if (recordUserId === userId) {
                const punchInTime = parseInt(record.punchInTime) || new Date(record.created).getTime()
                const punchDate = new Date(punchInTime)
                const dateKey = `${punchDate.getFullYear()}-${punchDate.getMonth()}-${punchDate.getDate()}`

                // Use dayType from backend only
                const dayType = record.dayType || 'No Punch Out'
                let dayValue = 0

                switch (dayType) {
                    case 'Full Day':
                        dayValue = 1
                        break
                    case 'Half Day':
                        dayValue = 0.5
                        break
                    case 'Absent':
                        dayValue = 0
                        break
                    case 'No Punch Out':
                        dayValue = 0 // Not counted in salary until admin changes dayType
                        break
                    default:
                        dayValue = 0
                }

                // Store day value and type (only if not already set or if current is better)
                if (!dayTypes[dateKey]) {
                    dayValues[dateKey] = dayValue
                    dayTypes[dateKey] = dayType
                }
            }
        })

        const values = Object.values(dayValues)
        const types = Object.values(dayTypes)
        const totalDays = values.reduce((sum, val) => sum + val, 0)

        // Count based on dayType only
        const fullDayCount = types.filter(t => t === 'Full Day').length
        const halfDayCount = types.filter(t => t === 'Half Day').length
        const noPunchOutCount = types.filter(t => t === 'No Punch Out').length
        const absentFromDayType = types.filter(t => t === 'Absent').length

        return { totalDays, fullDayCount, halfDayCount, noPunchOutCount, absentFromDayType }
    }

    const calculateSalary = (usersData, attendanceData, range) => {
        const totalDaysInRange = getDaysInRange(range.start, range.end)

        // For salary calculation, we need to consider the full month
        const monthStart = new Date(range.start.getFullYear(), range.start.getMonth(), 1)
        const monthEnd = new Date(range.start.getFullYear(), range.start.getMonth() + 1, 0)
        const totalDaysInMonth = monthEnd.getDate()

        // Filter out users with zero salary or inactive status
        const activeUsersWithSalary = usersData.filter(user =>
            user.salary > 0 && user.status !== "Inactive"
        )

        // Pre-process attendance data for faster lookup: userId -> dateKey -> record
        const attendanceMap = {}
        attendanceData.forEach(record => {
            const userId = record.userId?._id || record.userId
            if (!attendanceMap[userId]) attendanceMap[userId] = {}

            const punchInTime = parseInt(record.punchInTime) || new Date(record.created).getTime()
            const punchDate = new Date(punchInTime)
            const dateKey = `${punchDate.getFullYear()}-${punchDate.getMonth()}-${punchDate.getDate()}`

            // If multiple records exist for same day, prioritize the one with 'Present' or 'Absent' status
            // (Simple overwrite for now, assuming latest or valid one is sufficient)
            attendanceMap[userId][dateKey] = record
        })

        const calculatedData = activeUsersWithSalary.map((user, index) => {
            const monthlySalary = user.salary || 0
            const weekOff = user.weekOff || 'sunday'
            const weekOffIndex = weekDays.indexOf(weekOff.toLowerCase())

            // Per Day Salary
            const perDaySalary = totalDaysInMonth > 0 ? monthlySalary / totalDaysInMonth : 0

            // Initialize counters
            let fullDayCount = 0
            let halfDayCount = 0
            let noPunchOutCount = 0
            let absentRecordCount = 0
            let implicitAbsentCount = 0 // Days not attended (no record)
            let weekOffCountInRange = 0
            let expectedWorkingDaysInRange = 0

            // Iterate through every day in the range
            const current = new Date(range.start)
            const end = new Date(range.end)
            // Normalize start time to 00:00:00 to avoid skipping first day if range.start has time
            current.setHours(0, 0, 0, 0)
            end.setHours(23, 59, 59, 999)

            while (current <= end) {
                const dateKey = `${current.getFullYear()}-${current.getMonth()}-${current.getDate()}`
                const dayOfWeek = current.getDay()
                const isWeekOff = dayOfWeek === weekOffIndex

                if (isWeekOff) {
                    weekOffCountInRange++
                } else {
                    expectedWorkingDaysInRange++
                }

                const record = attendanceMap[user._id]?.[dateKey]

                if (record) {
                    // Determine Day Type correctly
                    let dayType = record.dayType
                    if (record.status === 'Absent') dayType = 'Absent'
                    if (!dayType) dayType = 'No Punch Out' // Fallback

                    switch (dayType) {
                        case 'Full Day':
                            fullDayCount++
                            break
                        case 'Half Day':
                            halfDayCount++
                            break
                        case 'Absent':
                            absentRecordCount++
                            break
                        case 'No Punch Out':
                        default:
                            noPunchOutCount++
                            break
                    }
                } else {
                    // No Record
                    if (!isWeekOff) {
                        implicitAbsentCount++
                    }
                }

                // Move to next day
                current.setDate(current.getDate() + 1)
            }

            // Payable Days = Full Day + (Half Day × 0.5) + Week Offs
            const payableDays = fullDayCount + (halfDayCount * 0.5) + weekOffCountInRange

            // Total Absent for Display = Explicit Absent Records + Implicit Absents
            const totalAbsentCount = absentRecordCount + implicitAbsentCount

            // Non-Payable Days (for deduction)
            // Strictly: Absent Records + No Punch Out + Implicit Absents
            // Note: If an Absent Record is on a Week Off, it counts in absentRecordCount
            // But usually we don't deduct for week offs.
            // However, the previous logic (and requested logic) seems to rely on counts.
            // Let's assume 'absentRecordCount' includes Week Off absents if they exist.
            // Standard behavior: Week Offs are paid. Absents on Week Offs shouldn't reduce pay.
            // But if we simply sum them, they might.
            // To be safe and consistent with "Absent 2 showing 1":
            // We'll calculate Deduction based on the derived stats.

            const nonPayableDays = totalAbsentCount + noPunchOutCount

            // Expected Salary
            const expectedSalaryForRange = perDaySalary * expectedWorkingDaysInRange

            // Gross Pay
            const grossPay = Math.max(0, perDaySalary * payableDays)

            // Deduction
            // Deduction = Per Day Salary * Non-Payable Days?
            // OR Deduction = MaxPossible - GrossPay?
            // Let's stick to the previous formula structure but using new counts:
            // Deduction = Per Day Salary × Non-Payable Days
            const deduction = perDaySalary * nonPayableDays

            // TDS
            const tdsEligible = user.tdsEligible || false
            const tdsPercentage = user.tdsPercentage || 0
            const tdsAmount = tdsEligible ? (grossPay * tdsPercentage) / 100 : 0

            // Net Payable
            const netPayable = grossPay - tdsAmount

            return {
                sno: index + 1,
                name: user.name,
                username: user.username,
                monthlySalary: monthlySalary,
                weekOff: weekOff,
                totalDays: totalDaysInRange,
                weekOffDays: weekOffCountInRange,
                expectedWorkingDays: expectedWorkingDaysInRange,
                fullDayCount: fullDayCount,
                halfDays: halfDayCount,
                absentFromDayType: totalAbsentCount,
                noPunchOut: noPunchOutCount,
                payableDays: payableDays,
                nonPayableDays: nonPayableDays,
                perDaySalary: perDaySalary,
                expectedSalary: expectedSalaryForRange,
                deduction: deduction,
                grossPay: grossPay,
                tdsEligible: tdsEligible,
                tdsPercentage: tdsPercentage,
                tdsAmount: tdsAmount,
                netPayable: netPayable,
                userId: user._id
            }
        })

        setSalaryData(calculatedData)
    }

    const formatCurrency = (amount) => {
        return `₹${Math.round(amount).toLocaleString('en-IN')}`
    }

    // Generate daily attendance data for a specific user
    const generateDailyAttendance = (userId, employeeData) => {
        const range = getDateRange()
        const weekOff = employeeData.weekOff || 'sunday'
        const weekOffIndex = weekDays.indexOf(weekOff.toLowerCase())

        const dailyData = []
        const current = new Date(range.start)
        const end = new Date(range.end)

        while (current <= end) {
            const dateKey = `${current.getFullYear()}-${current.getMonth()}-${current.getDate()}`
            const dayOfWeek = current.getDay()
            const isWeekOff = dayOfWeek === weekOffIndex

            // Find attendance record for this day
            const dayAttendance = attendance.find(record => {
                const recordUserId = record.userId?._id || record.userId
                if (recordUserId !== userId) return false

                const punchInTime = parseInt(record.punchInTime) || new Date(record.created).getTime()
                const punchDate = new Date(punchInTime)
                const recordDateKey = `${punchDate.getFullYear()}-${punchDate.getMonth()}-${punchDate.getDate()}`

                return recordDateKey === dateKey
            })

            let status = 'Absent'
            let punchIn = '-'
            let punchOut = '-'
            let workingHours = '-'
            let dayType = 'Absent'
            let attendanceId = null
            let remarks = ''

            // First check if there's an attendance record (even on week off days)
            if (dayAttendance && dayAttendance.status === 'Present') {
                status = isWeekOff ? 'Present (Week Off)' : 'Present'
                attendanceId = dayAttendance._id
                remarks = dayAttendance.remarks || ''
                const punchInTime = parseInt(dayAttendance.punchInTime) || new Date(dayAttendance.created).getTime()
                const punchOutTime = parseInt(dayAttendance.punchOutTime)

                punchIn = new Date(punchInTime).toLocaleTimeString('en-IN', {
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: true
                })

                if (punchOutTime) {
                    punchOut = new Date(punchOutTime).toLocaleTimeString('en-IN', {
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: true
                    })
                    const hours = (punchOutTime - punchInTime) / (1000 * 60 * 60)
                    workingHours = hours.toFixed(2) + ' hrs'
                } else {
                    punchOut = 'No Punch Out'
                }

                // Use dayType from backend if available, otherwise keep default
                dayType = dayAttendance.dayType || 'No Punch Out'
            } else if (isWeekOff) {
                status = 'Week Off'
                dayType = 'Week Off'
            }

            dailyData.push({
                date: new Date(current),
                dateFormatted: current.toLocaleDateString('en-IN', {
                    weekday: 'short',
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric'
                }),
                dayName: weekDays[dayOfWeek].charAt(0).toUpperCase() + weekDays[dayOfWeek].slice(1),
                status,
                punchIn,
                punchOut,
                workingHours,
                dayType,
                isWeekOff,
                attendanceId,
                remarks
            })

            current.setDate(current.getDate() + 1)
        }

        return dailyData
    }

    // Handle view attendance click
    const handleViewAttendance = (employee) => {
        const dailyData = generateDailyAttendance(employee.userId, employee)
        setSelectedEmployee(employee)
        setDailyAttendanceData(dailyData)
        setShowAttendanceModal(true)
    }

    // Handle edit attendance click
    const handleEditAttendance = (dayData) => {
        setEditingAttendance(dayData)
        setEditFormData({
            dayType: dayData.dayType || '',
            remarks: dayData.remarks || ''
        })
        setShowEditModal(true)
    }

    // Handle edit form changes
    const handleEditFormChange = (field, value) => {
        setEditFormData(prev => ({
            ...prev,
            [field]: value
        }))
    }

    // Handle save edit
    const handleSaveEdit = async () => {
        // Validate remarks is required
        if (!editFormData.remarks || !editFormData.remarks.trim()) {
            toast.error('Remarks is required')
            return
        }

        // Validate dayType is required
        if (!editFormData.dayType) {
            toast.error('Day Type is required')
            return
        }

        try {
            let res

            if (editingAttendance?.attendanceId) {
                // Update existing attendance
                const json = {
                    _id: editingAttendance.attendanceId,
                    dayType: editFormData.dayType,
                    remarks: editFormData.remarks.trim()
                }
                res = await HitApi(json, updateAttendance)
            } else {
                // Create new attendance for absent day
                const absentDate = new Date(editingAttendance.date)
                absentDate.setHours(9, 0, 0, 0) // Default punch in at 9 AM

                const json = {
                    userId: selectedEmployee.userId,
                    unitIds: getSelectedUnit()?._id,
                    punchInTime: absentDate.getTime().toString(),
                    punchOutTime: null,
                    status: 'Present',
                    dayType: editFormData.dayType,
                    remarks: editFormData.remarks.trim(),
                    created: editingAttendance.date.toISOString()
                }
                res = await HitApi(json, addAttendance)
            }

            if (res?.statusCode === 200 || res?.statusCode === 201) {
                toast.success(editingAttendance?.attendanceId ? 'Attendance updated successfully' : 'Attendance added successfully')
                setShowEditModal(false)
                setEditingAttendance(null)

                // Refresh main data
                const range = getDateRange()
                const [usersRes, attendanceRes] = await Promise.all([
                    fetchUsers(),
                    fetchAttendance(range)
                ])

                if (usersRes && attendanceRes) {
                    calculateSalary(usersRes, attendanceRes, range)

                    // Update daily attendance data in modal with fresh data
                    if (selectedEmployee) {
                        const updatedDailyData = generateDailyAttendanceFromData(selectedEmployee.userId, selectedEmployee, attendanceRes)
                        setDailyAttendanceData(updatedDailyData)
                    }
                }
            } else {
                toast.error(res?.message || 'Failed to save attendance')
            }
        } catch (error) {
            console.error('Error saving attendance:', error)
            toast.error('Error saving attendance')
        }
    }

    // Generate daily attendance from fresh data
    const generateDailyAttendanceFromData = (userId, employeeData, attendanceData) => {
        const range = getDateRange()
        const weekOff = employeeData.weekOff || 'sunday'
        const weekOffIndex = weekDays.indexOf(weekOff.toLowerCase())

        const dailyData = []
        const current = new Date(range.start)
        const end = new Date(range.end)

        while (current <= end) {
            const dateKey = `${current.getFullYear()}-${current.getMonth()}-${current.getDate()}`
            const dayOfWeek = current.getDay()
            const isWeekOff = dayOfWeek === weekOffIndex

            const dayAttendance = attendanceData.find(record => {
                const recordUserId = record.userId?._id || record.userId
                if (recordUserId !== userId) return false

                const punchInTime = parseInt(record.punchInTime) || new Date(record.created).getTime()
                const punchDate = new Date(punchInTime)
                const recordDateKey = `${punchDate.getFullYear()}-${punchDate.getMonth()}-${punchDate.getDate()}`

                return recordDateKey === dateKey
            })

            let status = 'Absent'
            let punchIn = '-'
            let punchOut = '-'
            let workingHours = '-'
            let dayType = 'Absent'
            let attendanceId = null
            let remarks = ''

            // First check if there's an attendance record (even on week off days)
            if (dayAttendance && dayAttendance.status === 'Present') {
                status = isWeekOff ? 'Present (Week Off)' : 'Present'
                attendanceId = dayAttendance._id
                remarks = dayAttendance.remarks || ''
                const punchInTime = parseInt(dayAttendance.punchInTime) || new Date(dayAttendance.created).getTime()
                const punchOutTime = parseInt(dayAttendance.punchOutTime)

                punchIn = new Date(punchInTime).toLocaleTimeString('en-IN', {
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: true
                })

                if (punchOutTime) {
                    punchOut = new Date(punchOutTime).toLocaleTimeString('en-IN', {
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: true
                    })
                    const hours = (punchOutTime - punchInTime) / (1000 * 60 * 60)
                    workingHours = hours.toFixed(2) + ' hrs'
                } else {
                    punchOut = 'No Punch Out'
                }

                dayType = dayAttendance.dayType || 'No Punch Out'
            } else if (isWeekOff) {
                status = 'Week Off'
                dayType = 'Week Off'
            }

            dailyData.push({
                date: new Date(current),
                dateFormatted: current.toLocaleDateString('en-IN', {
                    weekday: 'short',
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric'
                }),
                dayName: weekDays[dayOfWeek].charAt(0).toUpperCase() + weekDays[dayOfWeek].slice(1),
                status,
                punchIn,
                punchOut,
                workingHours,
                dayType,
                isWeekOff,
                attendanceId,
                remarks
            })

            current.setDate(current.getDate() + 1)
        }

        return dailyData
    }

    const tableHeaders = [
        {
            title: "S.No",
            key: "sno",
            width: "50px",
            align: "left",
            priority: 1,
            render: (value) => (
                <span className="text-sm font-medium text-gray-900">{value}</span>
            )
        },
        {
            title: "Employee",
            key: "name",
            align: "left",
            priority: 1,
            render: (value, row) => (
                <div>
                    <div className="text-sm font-medium text-gray-900">{value}</div>
                    <div className="text-xs text-gray-500">@{row.username} • {row.weekOff}</div>
                </div>
            )
        },
        {
            title: "Salary",
            key: "monthlySalary",
            align: "left",
            priority: 1,
            render: (value, row) => (
                <div>
                    <div className="text-sm font-semibold text-gray-900">{formatCurrency(value)}</div>
                    <div className="text-xs text-gray-500">{formatCurrency(row.perDaySalary)}/day</div>
                </div>
            )
        },
        {
            title: "Days",
            key: "totalDays",
            align: "left",
            priority: 1,
            render: (value, row) => (
                <div className="text-sm">
                    <span className="font-medium text-gray-900">{row.totalDays} Total</span>
                    <span className="text-blue-600 ml-1">({row.weekOffDays} Off)</span>
                </div>
            )
        },
        {
            title: "Attendance",
            key: "fullDayCount",
            align: "left",
            priority: 1,
            render: (value, row) => (
                <div className="flex flex-wrap gap-1">
                    <span className={`px-2 py-0.5 text-xs font-semibold rounded ${value > 0 ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>{value}P</span>
                    <span className={`px-2 py-0.5 text-xs font-semibold rounded ${row.absentFromDayType > 0 ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-600'}`}>{row.absentFromDayType}A</span>
                    <span className={`px-2 py-0.5 text-xs font-semibold rounded ${row.halfDays > 0 ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-600'}`}>{row.halfDays}H</span>
                    <span className={`px-2 py-0.5 text-xs font-semibold rounded ${row.noPunchOut > 0 ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-600'}`}>{row.noPunchOut}NP</span>
                </div>
            )
        },
        {
            title: "Payable Days",
            key: "payableDays",
            align: "left",
            priority: 1,
            render: (value) => (
                <span className="text-sm font-semibold text-indigo-600">{value}</span>
            )
        },
        {
            title: "Deduction",
            key: "deduction",
            align: "left",
            priority: 1,
            render: (value) => (
                <span className={`text-sm font-medium ${value > 0 ? 'text-red-600' : 'text-gray-600'}`}>
                    {value > 0 ? `-${formatCurrency(value)}` : formatCurrency(0)}
                </span>
            )
        },
        {
            title: "Gross Pay",
            key: "grossPay",
            align: "left",
            priority: 1,
            render: (value) => (
                <span className="text-sm font-semibold text-blue-600">{formatCurrency(value)}</span>
            )
        },
        {
            title: "TDS",
            key: "tdsAmount",
            align: "left",
            priority: 1,
            render: (value, row) => (
                <div>
                    <span className={`text-sm font-medium ${value > 0 ? 'text-orange-600' : 'text-gray-600'}`}>
                        {value > 0 ? `-${formatCurrency(value)}` : formatCurrency(0)}
                    </span>
                    {row.tdsEligible && (
                        <div className="text-xs text-orange-500">{row.tdsPercentage}%</div>
                    )}
                </div>
            )
        },
        {
            title: "Net Payable",
            key: "netPayable",
            align: "left",
            priority: 1,
            render: (value) => (
                <span className="text-sm font-bold text-green-600">{formatCurrency(value)}</span>
            )
        },
        {
            title: "Action",
            key: "action",
            align: "center",
            priority: 1,
            render: (value, row) => (
                <button
                    onClick={() => handleViewAttendance(row)}
                    className="p-2 bg-blue-50 hover:bg-blue-100 text-blue-600 rounded-lg transition-all duration-200 border border-blue-200 hover:border-blue-300"
                    title="View Daily Attendance"
                >
                    <Eye className="w-4 h-4" />
                </button>
            )
        }
    ]

    // Calculate totals
    const totals = salaryData.reduce((acc, curr) => ({
        totalMonthlySalary: acc.totalMonthlySalary + curr.monthlySalary,
        totalExpectedSalary: acc.totalExpectedSalary + (curr.perDaySalary * curr.expectedWorkingDays),
        totalGrossPay: acc.totalGrossPay + curr.grossPay,
        totalDeduction: acc.totalDeduction + curr.deduction,
        totalTDS: acc.totalTDS + curr.tdsAmount,
        totalNetPayable: acc.totalNetPayable + curr.netPayable,
        totalFullDay: acc.totalFullDay + curr.fullDayCount,
        totalAbsent: acc.totalAbsent + curr.absentFromDayType,
        totalHalfDays: acc.totalHalfDays + curr.halfDays,
        totalNoPunchOut: acc.totalNoPunchOut + curr.noPunchOut,
        totalPayableDays: acc.totalPayableDays + curr.payableDays,
        totalExpectedDays: acc.totalExpectedDays + curr.expectedWorkingDays
    }), {
        totalMonthlySalary: 0,
        totalExpectedSalary: 0,
        totalGrossPay: 0,
        totalDeduction: 0,
        totalTDS: 0,
        totalNetPayable: 0,
        totalFullDay: 0,
        totalAbsent: 0,
        totalHalfDays: 0,
        totalNoPunchOut: 0,
        totalPayableDays: 0,
        totalExpectedDays: 0
    })

    // Export functionality
    const handleExportData = async (format) => {
        try {
            toast.loading('Preparing export...')

            if (salaryData.length === 0) {
                toast.dismiss()
                toast.error('No data to export')
                return
            }

            const exportHeaders = [
                { title: 'S.No', key: 'sno' },
                { title: 'Employee Name', key: 'name' },
                { title: 'Username', key: 'username' },
                { title: 'Week Off', key: 'weekOff' },
                { title: 'Monthly Salary', key: 'monthlySalary' },
                { title: 'Per Day Salary', key: 'perDaySalary' },
                { title: 'Total Days', key: 'totalDays' },
                { title: 'Week Off Days', key: 'weekOffDays' },
                { title: 'Expected Working Days', key: 'expectedWorkingDays' },
                { title: 'Full Days (Present)', key: 'fullDayCount' },
                { title: 'Half Days', key: 'halfDays' },
                { title: 'Absent Days', key: 'absentFromDayType' },
                { title: 'No Punch Out', key: 'noPunchOut' },
                { title: 'Payable Days', key: 'payableDays' },
                { title: 'Non-Payable Days', key: 'nonPayableDays' },
                { title: 'Expected Salary', key: 'expectedSalary' },
                { title: 'Deduction', key: 'deduction' },
                { title: 'Gross Pay', key: 'grossPay' },
                { title: 'TDS Eligible', key: 'tdsEligible' },
                { title: 'TDS Percentage', key: 'tdsPercentage' },
                { title: 'TDS Amount', key: 'tdsAmount' },
                { title: 'Net Payable', key: 'netPayable' }
            ]

            const flattenedData = salaryData.map((row) => ({
                sno: row.sno,
                name: row.name || 'N/A',
                username: row.username || 'N/A',
                weekOff: row.weekOff || 'N/A',
                monthlySalary: Number(row.monthlySalary).toFixed(2),
                perDaySalary: Number(row.perDaySalary).toFixed(2),
                totalDays: row.totalDays,
                weekOffDays: row.weekOffDays,
                expectedWorkingDays: row.expectedWorkingDays,
                fullDayCount: row.fullDayCount,
                halfDays: row.halfDays,
                absentFromDayType: row.absentFromDayType,
                noPunchOut: row.noPunchOut,
                payableDays: row.payableDays,
                nonPayableDays: row.nonPayableDays,
                expectedSalary: Number(row.expectedSalary).toFixed(2),
                deduction: Number(row.deduction).toFixed(2),
                grossPay: Number(row.grossPay).toFixed(2),
                tdsEligible: row.tdsEligible ? 'Yes' : 'No',
                tdsPercentage: row.tdsPercentage || 0,
                tdsAmount: Number(row.tdsAmount).toFixed(2),
                netPayable: Number(row.netPayable).toFixed(2)
            }))

            const filename = `attendance_report_${getDateRangeText().replace(/\s/g, '_')}_${new Date().toISOString().split('T')[0]}`

            toast.dismiss()

            if (format === 'csv') {
                exportToCSV(flattenedData, exportHeaders, `${filename}.csv`)
                toast.success(`Exported ${salaryData.length} records to CSV`)
            } else if (format === 'pdf') {
                exportToPDF(flattenedData, exportHeaders, `${filename}.pdf`, 'Attendance Report')
                toast.success(`Exported ${salaryData.length} records to PDF`)
            }
        } catch (error) {
            toast.dismiss()
            toast.error('Error exporting data')
            console.error('Export error:', error)
        }
    }

    return (
        <div className='p-5'>
            <PageHeader
                title={'Attendance Report'}
                description={'Manage and view attendance based salary calculation'}
            />

            {/* Header with Date Filter */}
            <div className='bg-white rounded-2xl shadow-xl p-6 mb-6'>
                <div className='flex flex-wrap items-center justify-between gap-4'>
                    <div className='flex items-center gap-3'>
                        <div className='bg-gradient-to-r from-blue-600 to-purple-600 p-3 rounded-xl shadow-lg'>
                            <Calendar className='w-6 h-6 text-white' />
                        </div>
                        <div>
                            <h2 className='text-xl font-bold text-gray-900'>Salary Report</h2>
                            <div className='flex items-center gap-2 mt-1 bg-blue-50 px-3 py-1 rounded-lg border border-blue-100'>
                                <Calendar className='w-4 h-4 text-blue-600' />
                                <span className='font-medium text-blue-700 text-sm'>{getDateRangeText()}</span>
                            </div>
                        </div>
                    </div>

                    <div className='flex items-center gap-2'>
                        <button
                            onClick={fetchData}
                            disabled={isLoading}
                            className='p-2.5 border-2 border-gray-200 rounded-xl bg-white text-gray-700 hover:bg-blue-50 hover:border-blue-300 transition-all shadow-sm disabled:opacity-50'
                            title='Refresh'
                        >
                            <RefreshCw className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`} />
                        </button>

                        <select
                            value={selectedFilter}
                            onChange={(e) => handleFilterChange(e.target.value)}
                            className='px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-700 font-medium cursor-pointer shadow-sm hover:border-blue-300 transition-all'
                        >
                            {filterOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Custom Date Range Picker */}
                {showCustomRange && (
                    <div className='bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-blue-200 rounded-xl p-4 mt-4'>
                        <div className='flex flex-wrap items-center gap-4'>
                            <div className='flex items-center gap-2'>
                                <Calendar className='w-5 h-5 text-blue-600' />
                                <span className='text-sm font-semibold text-gray-700'>Custom Range:</span>
                            </div>

                            <div className='flex items-center gap-2'>
                                <label className='text-sm text-gray-600 font-medium'>From:</label>
                                <input
                                    type='date'
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                    className='px-3 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white shadow-sm'
                                />
                            </div>

                            <div className='flex items-center gap-2'>
                                <label className='text-sm text-gray-600 font-medium'>To:</label>
                                <input
                                    type='date'
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                    className='px-3 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white shadow-sm'
                                />
                            </div>

                            <button
                                onClick={handleApplyCustomRange}
                                disabled={!startDate || !endDate}
                                className={`px-6 py-2 rounded-lg font-semibold transition-all shadow-md ${startDate && endDate
                                    ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:shadow-lg'
                                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                    }`}
                            >
                                Apply Filter
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Summary Cards */}
            <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4 mb-6'>
                <div className='bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl shadow-md p-4 border border-gray-200'>
                    <div className='flex items-center gap-3'>
                        <div className='bg-gradient-to-br from-gray-500 to-gray-600 p-2 rounded-lg'>
                            <Users className='w-5 h-5 text-white' />
                        </div>
                        <div>
                            <p className='text-xs text-gray-600'>Total Employees</p>
                            <p className='text-xl font-bold text-gray-900'>{salaryData.length}</p>
                        </div>
                    </div>
                </div>

                <div className='bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl shadow-md p-4 border border-blue-200'>
                    <div className='flex items-center gap-3'>
                        <div className='bg-gradient-to-br from-blue-500 to-blue-600 p-2 rounded-lg'>
                            <DollarSign className='w-5 h-5 text-white' />
                        </div>
                        <div>
                            <p className='text-xs text-blue-600'>Expected Salary</p>
                            <p className='text-xl font-bold text-blue-900'>{formatCurrency(totals.totalExpectedSalary)}</p>
                        </div>
                    </div>
                </div>

                <div className='bg-gradient-to-br from-red-50 to-red-100 rounded-xl shadow-md p-4 border border-red-200'>
                    <div className='flex items-center gap-3'>
                        <div className='bg-gradient-to-br from-red-500 to-red-600 p-2 rounded-lg'>
                            <TrendingDown className='w-5 h-5 text-white' />
                        </div>
                        <div>
                            <p className='text-xs text-red-600'>Total Deduction</p>
                            <p className='text-xl font-bold text-red-900'>{formatCurrency(totals.totalDeduction)}</p>
                        </div>
                    </div>
                </div>

                <div className='bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl shadow-md p-4 border border-purple-200'>
                    <div className='flex items-center gap-3'>
                        <div className='bg-gradient-to-br from-purple-500 to-purple-600 p-2 rounded-lg'>
                            <DollarSign className='w-5 h-5 text-white' />
                        </div>
                        <div>
                            <p className='text-xs text-purple-600'>Gross Pay</p>
                            <p className='text-xl font-bold text-purple-900'>{formatCurrency(totals.totalGrossPay)}</p>
                        </div>
                    </div>
                </div>

                <div className='bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl shadow-md p-4 border border-orange-200'>
                    <div className='flex items-center gap-3'>
                        <div className='bg-gradient-to-br from-orange-500 to-orange-600 p-2 rounded-lg'>
                            <TrendingDown className='w-5 h-5 text-white' />
                        </div>
                        <div>
                            <p className='text-xs text-orange-600'>Total TDS</p>
                            <p className='text-xl font-bold text-orange-900'>{formatCurrency(totals.totalTDS)}</p>
                        </div>
                    </div>
                </div>

                <div className='bg-gradient-to-br from-green-50 to-green-100 rounded-xl shadow-md p-4 border border-green-200'>
                    <div className='flex items-center gap-3'>
                        <div className='bg-gradient-to-br from-green-500 to-green-600 p-2 rounded-lg'>
                            <CheckCircle className='w-5 h-5 text-white' />
                        </div>
                        <div>
                            <p className='text-xs text-green-600'>Net Payable</p>
                            <p className='text-xl font-bold text-green-900'>{formatCurrency(totals.totalNetPayable)}</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Formula Explanation */}
            <div className='bg-yellow-50 rounded-xl border border-yellow-200 p-4 mb-6'>
                <h4 className='font-semibold text-yellow-800 mb-2'>Salary Calculation Formula (Based on Day Type):</h4>
                <div className='text-sm text-yellow-700 space-y-1'>
                    <p><strong>Per Day Salary</strong> = Monthly Salary ÷ Total Days in Month</p>
                    <p><strong>Payable Days</strong> = Full Day + (Half Day × 0.5)</p>
                    <p><strong>Non-Payable Days</strong> = Absent + No Punch Out + Days Not Attended</p>
                    <p><strong>Gross Pay</strong> = Per Day Salary × Payable Days</p>
                    <p><strong>TDS</strong> = Gross Pay × TDS Percentage (if TDS Eligible)</p>
                    <p><strong>Net Payable</strong> = Gross Pay - TDS</p>
                </div>
                <div className='mt-2 pt-2 border-t border-yellow-300 text-xs text-yellow-600'>
                    <strong>Day Type Values:</strong> Full Day = 1 | Half Day = 0.5 | Absent = 0 | No Punch Out = 0
                </div>
            </div>

            {/* Loading State */}
            {isLoading && (
                <div className='flex flex-col justify-center items-center py-12'>
                    <div className='relative'>
                        <div className='animate-spin rounded-full h-12 w-12 border-b-4 border-blue-600'></div>
                    </div>
                    <p className='mt-4 text-gray-600 font-medium'>Loading attendance data...</p>
                </div>
            )}

            {/* Table */}
            {!isLoading && (
                <div className='bg-white rounded-2xl shadow-xl p-6'>
                    <AppTable
                        TH={tableHeaders}
                        TD={salaryData}
                        isLoading={isLoading}
                        enableExport={true}
                        onExportData={handleExportData}
                        exportFileName="attendance_report"
                    />
                </div>
            )}

            {/* Daily Attendance Modal */}
            {showAttendanceModal && selectedEmployee && (
                <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2'>
                    <div className='bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col'>
                        {/* Modal Header - Compact */}
                        <div className='bg-gradient-to-r from-blue-600 to-purple-600 p-3 text-white flex-shrink-0'>
                            <div className='flex items-center justify-between'>
                                <div>
                                    <h3 className='text-base font-bold'>{selectedEmployee.name}</h3>
                                    <p className='text-blue-100 text-xs'>@{selectedEmployee.username} | Week Off: {selectedEmployee.weekOff} | {getDateRangeText()}</p>
                                </div>
                                <button
                                    onClick={() => setShowAttendanceModal(false)}
                                    className='p-1.5 hover:bg-white hover:bg-opacity-20 rounded-lg transition-all'
                                >
                                    <X className='w-5 h-5' />
                                </button>
                            </div>

                            {/* Summary Stats - Compact */}
                            <div className='grid grid-cols-5 gap-2 mt-2'>
                                <div className='bg-white bg-opacity-20 rounded-md p-1.5 text-center'>
                                    <p className='text-lg font-bold'>{selectedEmployee.fullDayCount}</p>
                                    <p className='text-[10px] text-blue-100'>Present</p>
                                </div>
                                <div className='bg-white bg-opacity-20 rounded-md p-1.5 text-center'>
                                    <p className='text-lg font-bold'>{selectedEmployee.absentFromDayType}</p>
                                    <p className='text-[10px] text-blue-100'>Absent</p>
                                </div>
                                <div className='bg-white bg-opacity-20 rounded-md p-1.5 text-center'>
                                    <p className='text-lg font-bold'>{selectedEmployee.halfDays}</p>
                                    <p className='text-[10px] text-blue-100'>Half Days</p>
                                </div>
                                <div className='bg-white bg-opacity-20 rounded-md p-1.5 text-center'>
                                    <p className='text-lg font-bold'>{selectedEmployee.noPunchOut}</p>
                                    <p className='text-[10px] text-blue-100'>No Punch Out</p>
                                </div>
                                <div className='bg-white bg-opacity-20 rounded-md p-1.5 text-center'>
                                    <p className='text-lg font-bold'>{selectedEmployee.weekOffDays}</p>
                                    <p className='text-[10px] text-blue-100'>Week Offs</p>
                                </div>
                            </div>
                        </div>

                        {/* Modal Body - Daily Attendance Table - Scrollable */}
                        <div className='flex-1 overflow-y-auto min-h-0'>
                            <table className='w-full text-xs'>
                                <thead className='bg-gray-50 sticky top-0'>
                                    <tr>
                                        <th className='px-2 py-2 text-left text-[10px] font-semibold text-gray-600 uppercase'>Date</th>
                                        <th className='px-2 py-2 text-left text-[10px] font-semibold text-gray-600 uppercase'>Status</th>
                                        <th className='px-2 py-2 text-left text-[10px] font-semibold text-gray-600 uppercase'>Punch In</th>
                                        <th className='px-2 py-2 text-left text-[10px] font-semibold text-gray-600 uppercase'>Punch Out</th>
                                        <th className='px-2 py-2 text-left text-[10px] font-semibold text-gray-600 uppercase'>Hours</th>
                                        <th className='px-2 py-2 text-left text-[10px] font-semibold text-gray-600 uppercase'>Day Type</th>
                                        {isSuperAdmin && (
                                            <th className='px-2 py-2 text-center text-[10px] font-semibold text-gray-600 uppercase'>Action</th>
                                        )}
                                    </tr>
                                </thead>
                                <tbody className='divide-y divide-gray-100'>
                                    {dailyAttendanceData.map((day, index) => (
                                        <tr key={index} className={`${day.status === 'Present (Week Off)' ? 'bg-teal-50' : day.isWeekOff ? 'bg-blue-50' : day.status === 'Absent' ? 'bg-red-50' : 'hover:bg-gray-50'}`}>
                                            <td className='px-2 py-1.5'>
                                                <div className='text-xs font-medium text-gray-900'>{day.dateFormatted}</div>
                                            </td>
                                            <td className='px-2 py-1.5'>
                                                <span className={`px-1.5 py-0.5 text-[10px] font-semibold rounded-full ${day.status === 'Present' ? 'bg-green-100 text-green-800' :
                                                    day.status === 'Present (Week Off)' ? 'bg-teal-100 text-teal-800' :
                                                        day.status === 'Week Off' ? 'bg-blue-100 text-blue-800' :
                                                            'bg-red-100 text-red-800'
                                                    }`}>
                                                    {day.status}
                                                </span>
                                            </td>
                                            <td className='px-2 py-1.5 text-xs text-gray-700'>{day.punchIn}</td>
                                            <td className='px-2 py-1.5 text-xs'>
                                                <span className={day.punchOut === 'No Punch Out' ? 'text-orange-600 font-medium' : 'text-gray-700'}>
                                                    {day.punchOut}
                                                </span>
                                            </td>
                                            <td className='px-2 py-1.5 text-xs text-gray-700'>{day.workingHours}</td>
                                            <td className='px-2 py-1.5'>
                                                <span className={`px-1.5 py-0.5 text-[10px] font-semibold rounded ${day.dayType === 'Full Day' ? 'bg-green-100 text-green-700' :
                                                    day.dayType === 'Half Day' ? 'bg-yellow-100 text-yellow-700' :
                                                        day.dayType === 'No Punch Out' ? 'bg-purple-100 text-purple-700' :
                                                            day.dayType === 'Absent' ? 'bg-red-100 text-red-700' :
                                                                day.dayType === 'Week Off' ? 'bg-blue-100 text-blue-700' :
                                                                    'bg-gray-100 text-gray-600'
                                                    }`}>
                                                    {day.dayType}
                                                </span>
                                            </td>
                                            {isSuperAdmin && (
                                                <td className='px-2 py-1.5 text-center'>
                                                    {(day.attendanceId || day.status === 'Absent') && (
                                                        <button
                                                            onClick={() => handleEditAttendance(day)}
                                                            className='p-1 bg-amber-50 hover:bg-amber-100 text-amber-600 rounded transition-all border border-amber-200'
                                                            title='Edit Attendance'
                                                        >
                                                            <Pencil className='w-3 h-3' />
                                                        </button>
                                                    )}
                                                </td>
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Modal Footer - Fixed at bottom */}
                        <div className='bg-gray-50 px-4 py-2.5 border-t flex-shrink-0'>
                            <div className='flex justify-between items-center'>
                                <div className='text-xs text-gray-600'>
                                    <span className='font-medium'>Net Payable:</span>
                                    <span className='ml-1.5 text-base font-bold text-green-600'>{formatCurrency(selectedEmployee.netPayable)}</span>
                                </div>
                                <button
                                    onClick={() => setShowAttendanceModal(false)}
                                    className='px-4 py-1.5 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg text-sm font-medium transition-all'
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Attendance Modal */}
            {showEditModal && editingAttendance && (
                <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4'>
                    <div className='bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden'>
                        {/* Edit Modal Header */}
                        <div className='bg-gradient-to-r from-amber-500 to-orange-500 p-5 text-white'>
                            <div className='flex items-center justify-between'>
                                <div>
                                    <h3 className='text-lg font-bold'>Edit Attendance</h3>
                                    <p className='text-amber-100 text-sm'>{editingAttendance.dateFormatted}</p>
                                </div>
                                <button
                                    onClick={() => {
                                        setShowEditModal(false)
                                        setEditingAttendance(null)
                                    }}
                                    className='p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-all'
                                >
                                    <X className='w-5 h-5' />
                                </button>
                            </div>
                        </div>

                        {/* Edit Modal Body */}
                        <div className='p-6 space-y-4'>
                            {/* Day Type */}
                            <div>
                                <label className='block text-sm font-medium text-gray-700 mb-1'>
                                    Day Type <span className='text-red-500'>*</span>
                                </label>
                                <select
                                    value={editFormData.dayType}
                                    onChange={(e) => handleEditFormChange('dayType', e.target.value)}
                                    className='w-full px-4 py-2.5 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent bg-white'
                                >
                                    <option value=''>Select Day Type</option>
                                    <option value='Full Day'>Full Day</option>
                                    <option value='Half Day'>Half Day</option>
                                    <option value='Absent'>Absent</option>
                                    <option value='No Punch Out'>No Punch Out</option>
                                </select>
                            </div>

                            {/* Remarks */}
                            <div>
                                <label className='block text-sm font-medium text-gray-700 mb-1'>
                                    Remarks <span className='text-red-500'>*</span>
                                </label>
                                <textarea
                                    value={editFormData.remarks}
                                    onChange={(e) => handleEditFormChange('remarks', e.target.value)}
                                    placeholder='Enter reason for changing attendance...'
                                    rows={3}
                                    className='w-full px-4 py-2.5 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent resize-none'
                                />
                            </div>
                        </div>

                        {/* Edit Modal Footer */}
                        <div className='bg-gray-50 px-6 py-4 border-t flex justify-end gap-3'>
                            <button
                                onClick={() => {
                                    setShowEditModal(false)
                                    setEditingAttendance(null)
                                }}
                                className='px-5 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-medium transition-all'
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSaveEdit}
                                className='px-5 py-2 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white rounded-lg font-medium transition-all shadow-md'
                            >
                                Save Changes
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default AttendanceReport
